import React from "react";
import { PlanFeatureProperties } from "@/src/api/Plan";
import { isZoneSymbol, PLAN_ZONES, PLAN_AREAS, PlanZoneSymbol, PlanAreaSymbol } from "@/src/lib/planStyles";

/**
 * Panel szczegółów feature'ów planu — pokazywany po kliknięciu w mapę.
 *
 * Dostaje TABLICĘ feature'ów (posortowaną wg priorytetu: strefa → obszary
 * → POG). Pierwszy element to STREFA PLANISTYCZNA (jeśli kliknięto w taką)
 * — pełen zestaw atrybutów. Pozostałe to obszary specjalne nakładające się
 * na strefę — w sekcji "Inne warstwy" pokazujemy zwięzłą listę
 * (typ obszaru + oznaczenie).
 *
 * Atrybuty wg wymogów usera:
 * - Strefa planistyczna: nazwa, oznaczenie, symbol, profile podstawowy
 *   i dodatkowy, intensywność/powierzchnia/wysokość zabudowy (n/d w POG —
 *   ustalane w MPZP), powierzchnia biologicznie czynna (z rozporządzenia).
 * - Obszary specjalne (POG/OUZ/OZS/OSD): typ + oznaczenie.
 */

export type PlanFeatureDetailsProps = {
  features: PlanFeatureProperties[];
  /** Zamknięcie panelu (X w prawym górnym rogu). */
  onClose?: () => void;
};

function PlanFeatureDetails({ features, onClose }: PlanFeatureDetailsProps) {
  if (!features || features.length === 0) {
    return (
      <div style={emptyStyle}>
        <em>Kliknij w obszar na mapie, żeby zobaczyć szczegóły</em>
      </div>
    );
  }

  // Pierwsza strefa planistyczna jako primary; jeśli klik trafił tylko w
  // obszar specjalny (np. POG poza strefą), primary = ten pierwszy element.
  const primary = features[0];
  const others = features.slice(1);
  const isPrimaryZone = isZoneSymbol(primary.symbol);
  const zoneStyle = isPrimaryZone ? PLAN_ZONES[primary.symbol as PlanZoneSymbol] : null;
  const areaStyle = primary.symbol in PLAN_AREAS ? PLAN_AREAS[primary.symbol as PlanAreaSymbol] : null;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {zoneStyle && (
            <span
              style={{
                display: "inline-block",
                width: 22,
                height: 22,
                background: zoneStyle.fill,
                border: `1px solid ${zoneStyle.stroke}`,
                flexShrink: 0,
                borderRadius: 3,
              }}
            />
          )}
          {areaStyle && !zoneStyle && (
            <span
              style={{
                display: "inline-block",
                width: 22,
                height: 22,
                background: "rgba(83,83,83,0.15)",
                border: `2px dashed ${areaStyle.stroke}`,
                flexShrink: 0,
                borderRadius: 3,
              }}
            />
          )}
          <div>
            <div style={{ fontFamily: "monospace", fontWeight: 600, fontSize: 14 }}>
              {primary.oznaczenie || primary.symbol}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              {(zoneStyle?.name || areaStyle?.name || primary.nazwaLabel || primary.symbol)}
            </div>
          </div>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Zamknij">
            ✕
          </button>
        )}
      </div>

      {/* SEKCJA GŁÓWNA — atrybuty zależne od typu primary */}
      {isPrimaryZone ? (
        <>
          <dl style={dlStyle}>
            <dt>Oznaczenie</dt>
            <dd><code>{primary.oznaczenie || "—"}</code></dd>
            <dt>Symbol</dt>
            <dd><code>{primary.symbol}</code></dd>
            <dt>Nazwa strefy</dt>
            <dd>{primary.nazwaLabel || zoneStyle?.name || "—"}</dd>
            <dt>Powierzchnia</dt>
            <dd>{formatArea(primary.area_m2)}</dd>
            <dt>Min. pow. biol. czynna</dt>
            <dd>{formatPbc(primary.attributes?.minUdzialPowierzchniBiologicznieCzynnej, zoneStyle?.minPbc)}</dd>
            <dt>Maks. intensywność zabudowy</dt>
            <dd>{formatIntensity(primary.attributes?.maksNadziemnaIntensywnoscZabudowy)}</dd>
            <dt>Maks. udział pow. zabudowy</dt>
            <dd>{formatPercent(primary.attributes?.maksUdzialPowierzchniZabudowy)}</dd>
            <dt>Maks. wysokość zabudowy</dt>
            <dd>{formatMeters(primary.attributes?.maksWysokoscZabudowy)}</dd>
          </dl>

          {primary.profilePodstawowyLabels && primary.profilePodstawowyLabels.length > 0 && (
            <div style={profileSectionStyle}>
              <h4 style={profileTitleStyle}>
                Profil podstawowy ({primary.profilePodstawowyLabels.length})
              </h4>
              <ul style={profileListStyle}>
                {primary.profilePodstawowyLabels.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}

          {primary.profileDodatkowyLabels && primary.profileDodatkowyLabels.length > 0 && (
            <div style={profileSectionStyle}>
              <h4 style={profileTitleStyle}>
                Profil dodatkowy ({primary.profileDodatkowyLabels.length})
              </h4>
              <ul style={profileListStyle}>
                {primary.profileDodatkowyLabels.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        // Primary jest obszarem specjalnym (POG/OUZ/OZS/OSD bez strefy pod spodem).
        <dl style={dlStyle}>
          <dt>Oznaczenie</dt>
          <dd><code>{primary.oznaczenie || "—"}</code></dd>
          <dt>Symbol</dt>
          <dd><code>{primary.symbol}</code></dd>
          <dt>Typ obszaru</dt>
          <dd>{areaStyle?.name || primary.featureType}</dd>
        </dl>
      )}

      {/* SEKCJA DODATKOWA — pozostałe warstwy w punkcie kliknięcia (zwykle
          obszary specjalne nakładające się na strefę). */}
      {others.length > 0 && (
        <div style={othersSectionStyle}>
          <h4 style={profileTitleStyle}>
            W tym punkcie znajduje się również ({others.length})
          </h4>
          <ul style={othersListStyle}>
            {others.map((f, i) => {
              const fAreaStyle = f.symbol in PLAN_AREAS ? PLAN_AREAS[f.symbol as PlanAreaSymbol] : null;
              const fZoneStyle = isZoneSymbol(f.symbol) ? PLAN_ZONES[f.symbol as PlanZoneSymbol] : null;
              const typeName = fAreaStyle?.name || fZoneStyle?.name || f.featureType;
              return (
                <li key={i} style={otherItemStyle}>
                  {fZoneStyle && (
                    <span style={{
                      display: "inline-block", width: 14, height: 14,
                      background: fZoneStyle.fill,
                      border: `1px solid ${fZoneStyle.stroke}`,
                      flexShrink: 0, borderRadius: 2,
                    }} />
                  )}
                  {fAreaStyle && (
                    <span style={{
                      display: "inline-block", width: 14, height: 14,
                      background: "rgba(83,83,83,0.1)",
                      border: `2px dashed ${fAreaStyle.stroke}`,
                      flexShrink: 0, borderRadius: 2,
                    }} />
                  )}
                  <span style={{ fontFamily: "monospace", fontWeight: 600, minWidth: 50 }}>
                    {f.oznaczenie || f.symbol}
                  </span>
                  <span style={{ flex: 1, color: "#374151" }}>{typeName}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function formatArea(m2: number | null | undefined): string {
  if (m2 == null) return "—";
  if (m2 >= 10_000) return `${(m2 / 10_000).toFixed(2)} ha (${Math.round(m2).toLocaleString("pl-PL")} m²)`;
  return `${Math.round(m2).toLocaleString("pl-PL")} m²`;
}

/**
 * Wartości zabudowy z GML mogą być:
 *  - liczbą (np. 0.8 dla intensywności bez jednostki)
 *  - obiektem `{ value, uom }` (np. { value: 12, uom: "m" } dla wysokości)
 *  - stringiem
 * Zwracamy `null` gdy brak — caller pokazuje "n/d".
 */
function readNumeric(v: unknown): { value: number; uom: string | null } | null {
  if (v == null) return null;
  if (typeof v === "number" && Number.isFinite(v)) return { value: v, uom: null };
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? { value: n, uom: null } : null;
  }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    const raw = o.value;
    const n = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(n)) return null;
    const uom = typeof o.uom === "string" ? o.uom : null;
    return { value: n, uom };
  }
  return null;
}

const NA_MPZP = "n/d (ustalane w MPZP)";

function formatIntensity(v: unknown): React.ReactNode {
  const r = readNumeric(v);
  if (!r) return <span style={{ color: "#6b7280" }}>{NA_MPZP}</span>;
  // Intensywność: liczba bez jednostki, formatowana z 2 miejscami po przecinku.
  return r.value.toFixed(2).replace(".", ",");
}

function formatPercent(v: unknown): React.ReactNode {
  const r = readNumeric(v);
  if (!r) return <span style={{ color: "#6b7280" }}>{NA_MPZP}</span>;
  // GML zapisuje udział jako ułamek (0..1). Jeśli wartość > 1, traktujemy
  // jako już-procenty (rzadkie, ale niektóre gminy zapisują niezgodnie ze schemą).
  const pct = r.value <= 1 ? r.value * 100 : r.value;
  return `${pct.toFixed(0)} %`;
}

function formatPbc(gmlValue: unknown, rozporządzenieValue: number | null | undefined): React.ReactNode {
  const r = readNumeric(gmlValue);
  if (r) {
    const pct = r.value <= 1 ? r.value * 100 : r.value;
    return `${pct.toFixed(0)} % (z planu)`;
  }
  if (rozporządzenieValue != null) {
    return `${rozporządzenieValue}% (z rozporządzenia)`;
  }
  return "n/d";
}

function formatMeters(v: unknown): React.ReactNode {
  const r = readNumeric(v);
  if (!r) return <span style={{ color: "#6b7280" }}>{NA_MPZP}</span>;
  const unit = r.uom || "m";
  return `${r.value.toString().replace(".", ",")} ${unit}`;
}

const containerStyle: React.CSSProperties = {
  fontFamily: "system-ui, -apple-system, sans-serif",
  fontSize: 13,
  background: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: 14,
  maxHeight: "100%",
  overflow: "auto",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 8,
  paddingBottom: 10,
  borderBottom: "1px solid #e5e7eb",
  marginBottom: 10,
};

const closeBtnStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  fontSize: 16,
  cursor: "pointer",
  color: "#6b7280",
  padding: "0 4px",
};

const dlStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  columnGap: 12,
  rowGap: 5,
  margin: 0,
  fontSize: 12,
};

const profileSectionStyle: React.CSSProperties = {
  marginTop: 10,
  paddingTop: 10,
  borderTop: "1px solid #e5e7eb",
};

const profileTitleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "#374151",
  margin: "0 0 4px 0",
};

const profileListStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: 18,
  fontSize: 12,
  color: "#4b5563",
  lineHeight: 1.4,
};

const othersSectionStyle: React.CSSProperties = {
  marginTop: 12,
  paddingTop: 10,
  borderTop: "2px solid #e5e7eb",
  background: "#fafafa",
  margin: "12px -14px -14px -14px",
  padding: "10px 14px",
  borderBottomLeftRadius: 6,
  borderBottomRightRadius: 6,
};

const othersListStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: "none",
};

const otherItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 0",
  fontSize: 12,
};

const emptyStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#9ca3af",
  textAlign: "center",
  padding: 20,
};

export default PlanFeatureDetails;
